'use client'

import { track } from '../analytics'
import { events as analyticsEvents } from '../analytics-events'
import { createChatTiming } from '../hermes/chat-timing'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import {
  historicalMessageText,
  chatHistoryPageSize,
  interruptedChatRetryMessage,
  isTerminalRun,
  workspaceContinuationPrefix,
} from '../hermes/chat-schema'
import { activityTitle, activityVerb } from '../workspace/session-activity'
import {
  activeRunId,
  followRun,
  op,
  readSnapshot,
  openedSession,
  sessionId as sessionIdPattern,
  waitingActivity,
  type ChatSnapshot,
  type SessionScope,
} from './chat'
import { isUserMessage, messageText, contentParts, type ContentPart, type UserMessage } from './agent-contract'
import { RelayError, isPermanentSendFailure, type PendingSend } from './ops'
import { ops, workspaceId } from './workspace'

type ToolPart = {
  type: 'dynamic-tool'
  toolCallId: string
  toolName: string
  input: unknown
  output: unknown
  state: 'output-available'
  partial?: never
  toolMetadata?: never
}
type Message = {
  id: string
  role: 'user' | 'assistant'
  parts: (ContentPart | { type: 'reasoning'; text: string } | ToolPart)[]
  /** `queued`: the workspace has not started a run for this message yet. */
  metadata?: { status: 'complete' | 'queued' | 'streaming' | 'failed' }
}
type Event = { type: string; data: { turnId?: string; message?: string }; meta: { id: string; at: string } }
function chatEvents(requests: ChatSnapshot['requests']): Event[] {
  return requests.flatMap((row) => {
    const start: Event = {
      type: 'turn.started',
      data: { turnId: row.request_id },
      meta: { id: row.request_id + ':start', at: row.created_at },
    }
    if (!row.terminal) return [start]
    const type =
      row.terminal.status === 'completed'
        ? 'turn.completed'
        : row.terminal.status === 'cancelled'
          ? 'turn.cancelled'
          : 'turn.failed'
    return [
      start,
      {
        type,
        data: {
          turnId: row.request_id,
          ...(type === 'turn.failed'
            ? {
                message:
                  row.terminal.run_id === null && typeof row.terminal.error === 'string'
                    ? row.terminal.error
                    : row.terminal.error_surface?.code === 'billing'
                      ? 'The model request was blocked by a billing or credit limit. Choose a lower-cost model or check Usage and credits before sending again.'
                      : 'Hermes could not finish this run. Its history and original action receipts are retained.',
              }
            : {}),
        },
        meta: { id: row.request_id + ':terminal', at: row.created_at },
      },
    ]
  })
}
function mergeHistory(previous: ChatSnapshot['messages'], next: ChatSnapshot['messages']) {
  return [...new Map([...previous, ...next].map((message) => [String(message.id), message])).values()].sort(
    (a, b) => Number(a.id) - Number(b.id)
  )
}
export type HermesChatOptions = {
  enabled: boolean
  conversationId?: string | undefined
  scope: SessionScope
  /** Null for URL-owned IDE chats; a stable owner/league key for a retained embedded chat. */
  sessionKey: string | null
  clientId: string
  prepareWorkspace: (signal?: AbortSignal) => Promise<string>
  onSessionChange: (session: { sessionId: string }) => void
  onEvent: (event: Event) => void
}
export type SendSettings = {
  clientContext: Record<string, unknown>
  /** A persisted initial intent keeps its native operation identity across browser reloads. */
  requestId?: string
  /** Per-run guidance for the agent, kept out of the visible message. */
  instructions?: string
}

/** The relay owns delivery and observation; the workspace owns execution. Nothing is kept on the IDE server. */
/** A waiting request is read this long after its resume time, so the read lands after the workspace may resume it. */
const waitingResumeMargin = 5000
/** Reads after the resume time until the request leaves `waiting`: the resume itself takes a moment. */
const waitingBackoff = [60000, 120000, 300000]
/** The longest delay `setTimeout` honours (a signed 32-bit millisecond count, about 24.8 days). */
const maxTimerDelay = 2 ** 31 - 1

export function useHermesChat(options: HermesChatOptions) {
  const current = useRef(options)
  current.current = options
  const id = useRef(options.conversationId)
  const boundWorkspace = useRef<string | undefined>(undefined)
  const streamConnected = useRef(false)
  const lastReadAt = useRef(0)
  const [snapshot, setSnapshot] = useState<ChatSnapshot>()
  const [outgoing, setOutgoing] = useState<{ requestId: string; message: UserMessage; afterMessageId: number }>()
  // From the send until the read that shows its run, when neither the bubble nor the snapshot can say so.
  const [sending, setSending] = useState(false)
  const [olderOffset, setOlderOffset] = useState(0)
  const [historyComplete, setHistoryComplete] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [status, setStatus] = useState<'ready' | 'submitted' | 'streaming' | 'resuming' | 'error'>('ready')
  const [error, setError] = useState('')
  const [checking, setChecking] = useState(false)
  const [observationFailed, setObservationFailed] = useState(false)
  const [activity, setActivity] = useState('')
  const [streamed, setStreamed] = useState<{ runId: string; afterMessageId: number; text: string }>()
  const [stopping, setStopping] = useState(false)
  const [decidingApproval, setDecidingApproval] = useState(false)
  const [stoppedLocally, setStoppedLocally] = useState(false)
  const [generation, setGeneration] = useState(0)
  const timing = useRef<ReturnType<typeof createChatTiming> | undefined>(undefined)
  /** A send the workspace has not acknowledged; its op id is reused by every retry and kept in localStorage by the ops client. */
  const pending = useRef<{ requestId: string; send: PendingSend; workspaceId: string | undefined } | undefined>(
    undefined
  )
  /** Stop was asked for before the workspace acknowledged the send; it applies to the run the acknowledgement names. */
  const stopRequested = useRef(false)
  const reading = useRef<Promise<ChatSnapshot | undefined> | null>(null)
  const rejectedMessage = useRef(false)
  const observation = useRef<AbortSignal | undefined>(undefined)
  const mounted = useRef(true)
  const seenEvents = useRef(new Set<string>())
  /** Requests that reached a run. A resumed request is briefly without one; its message is already in history. */
  const startedRequests = useRef(new Set<string>())
  /** The open request's `resume_after` while it waits, read by the slow follow-up after the observation deadline. */
  const waitingResumeAfter = useRef<string | null>(null)
  const fail = useCallback((cause: unknown) => {
    const error = cause instanceof Error ? cause : Error('Hermes observation failed.')
    if (mounted.current) {
      timing.current?.mark('error_observed')
      setError(error.message)
      setStatus('error')
    }
  }, [])
  /** Both surfaces use the same session, outbox, and observation lifecycle. */
  const prepare = useCallback(async (signal?: AbortSignal) => {
    const workspace = await current.current.prepareWorkspace(signal)
    signal?.throwIfAborted()
    let nextSession = id.current
    if (current.current.sessionKey) {
      const key = `ide:${workspace}:chat:${current.current.clientId}:${current.current.scope.coworld ?? 'workspace'}:${current.current.scope.league ?? ''}:${current.current.sessionKey}`
      let openId = ops.options.storage.getItem(key)
      if (!openId) {
        openId = crypto.randomUUID()
        ops.options.storage.setItem(key, openId)
      }
      const opened = await ops.json(
        workspace,
        op(
          {
            name: 'session.open',
            args: {
              client: { id: current.current.clientId, kind: 'web' },
              coworld: current.current.scope.coworld,
              league: current.current.scope.league,
            },
          },
          openId
        ),
        openedSession,
        signal
      )
      nextSession = opened.session_id
    }
    if (boundWorkspace.current && (boundWorkspace.current !== workspace || id.current !== nextSession)) {
      setSnapshot(undefined)
      setOutgoing(undefined)
      setStreamed(undefined)
      setOlderOffset(0)
      setHistoryComplete(false)
      setActivity('')
      setStoppedLocally(false)
      seenEvents.current.clear()
      timing.current = undefined
      stopRequested.current = false
      reading.current = null
      if (pending.current?.workspaceId !== workspace) pending.current = undefined
    }
    id.current = nextSession
    boundWorkspace.current = workspace
    if (nextSession) current.current.onSessionChange({ sessionId: nextSession })
    return workspace
  }, [])
  const refresh = useCallback(async (minimumGap = 2000) => {
    if (!current.current.enabled || !id.current) return
    if (reading.current) return reading.current
    const conversationId = id.current
    const signal = observation.current
    if (signal?.aborted) return
    const deadline = AbortSignal.timeout(20 * 60000)
    const readSignal = signal ? AbortSignal.any([signal, deadline]) : deadline
    const work = (async () => {
      // All observation paths share the same minimum gap, including event recovery.
      await new Promise((resolve) => setTimeout(resolve, Math.max(0, lastReadAt.current + minimumGap - Date.now())))
      if (!mounted.current || signal?.aborted || id.current !== conversationId) return
      const workspace = await workspaceId()
      boundWorkspace.current = workspace
      let value: ChatSnapshot | undefined
      // A lost connection is retried a bounded number of times; every other failure is reported as it is.
      for (let attempt = 0; value === undefined; attempt++) {
        value = await readSnapshot(ops, workspace, conversationId, current.current.scope, undefined, readSignal).catch(
          async (cause: unknown) => {
            if (
              readSignal.aborted ||
              !(cause instanceof RelayError && cause.kind === 'transport_unavailable') ||
              attempt === 3
            )
              throw cause
            setActivity('Reconnecting to your saved request…')
            await new Promise((resolve) => setTimeout(resolve, 2000 * 2 ** attempt))
            return undefined
          }
        )
        if (
          !mounted.current ||
          signal?.aborted ||
          id.current !== conversationId ||
          boundWorkspace.current !== workspace
        )
          return
      }
      setObservationFailed(false)
      setSnapshot((previous) => ({ ...value, messages: mergeHistory(previous?.messages ?? [], value.messages) }))
      setError('')
      for (const row of value.requests) if (row.run_id) startedRequests.current.add(row.request_id)
      const active = value.requests.find((row) => !row.terminal)
      setStatus(active ? (active.run_id && !active.waiting ? 'streaming' : 'submitted') : 'ready')
      const observed = value.requests.find((row) => row.request_id === timing.current?.requestId)
      if (observed?.terminal)
        timing.current?.mark('terminal_observed', {
          outcome: observed.terminal.status,
          ...(observed.run_id ? { run_id: observed.run_id } : {}),
        })
      if (observed?.run_id) timing.current?.mark('run_observed', { run_id: observed.run_id })
      setActivity(
        active?.waiting ? waitingActivity(active.waiting) : active && !active.run_id ? 'Starting your request…' : ''
      )
      if (!active) setStopping(false)
      for (const event of chatEvents(value.requests))
        if (!seenEvents.current.has(event.meta.id)) {
          seenEvents.current.add(event.meta.id)
          current.current.onEvent(event)
        }
      return value
    })().catch((cause: unknown) => {
      if (signal?.aborted) return undefined
      if (mounted.current && id.current === conversationId) setObservationFailed(true)
      throw cause
    })
    reading.current = work
    try {
      return await work
    } finally {
      lastReadAt.current = Date.now()
      if (reading.current === work) reading.current = null
    }
  }, [])
  const refreshAfterEvent = useCallback(
    async (minimumGap = 2000) => {
      const conversationId = id.current
      // Events describe writes newer than an in-flight snapshot. Always read again,
      // even if that snapshot marks the run terminal and detaches its event stream.
      await Promise.allSettled([reading.current])
      if (mounted.current && id.current === conversationId) await refresh(minimumGap)
    },
    [refresh]
  )
  /** Deliver the unacknowledged send with its original op id, then read the conversation again. */
  const resume = useCallback(
    async (signal?: AbortSignal) => {
      let accepted = false
      setStatus('resuming')
      setError('')
      await (async () => {
        if (pending.current) {
          const retained = pending.current
          const workspace = await prepare(signal)
          if (retained.workspaceId && retained.workspaceId !== workspace)
            throw new RelayError(
              'workspace_retired',
              'This message belongs to a reset workspace and was not replayed.',
              410
            )
          retained.workspaceId = workspace
          const admitted = await ops.send(workspace, retained.send, retained.requestId)
          accepted = true
          if (admitted.run_id) {
            startedRequests.current.add(admitted.requestId)
            setOutgoing((value) => value && { ...value })
          }
          id.current = admitted.session_id
          current.current.onSessionChange({ sessionId: admitted.session_id })
          timing.current?.mark('admission_received', { workspace_id: workspace })
          pending.current = undefined
          setStoppedLocally(false)
          if (stopRequested.current) {
            stopRequested.current = false
            await ops.json(workspace, op({ name: 'session.cancel', args: { op_id: admitted.requestId } }), z.unknown())
            setStoppedLocally(true)
          }
        }
        await refresh()
        setGeneration((value) => value + 1)
      })().catch((cause: unknown) => {
        if (observation.current?.aborted) return
        // Only a typed permanent refusal proves these exact bytes cannot have been admitted.
        // Unknown delivery or lifecycle failures keep the same request for reconciliation.
        if (!accepted && isPermanentSendFailure(cause)) {
          if (pending.current?.workspaceId) ops.discard(pending.current.workspaceId, pending.current.requestId)
          rejectedMessage.current = true
          pending.current = undefined
          setOutgoing(undefined)
        }
        fail(cause)
        // Observation can fail after admission. Keep that error visible without restoring an accepted message as a draft.
        if (!accepted) throw cause
      })
    },
    [refresh, fail, prepare]
  )
  const connect = useCallback(
    async (signal = observation.current) => {
      setStatus('resuming')
      const deadline = AbortSignal.timeout(20 * 60000)
      const lease = signal ? AbortSignal.any([signal, deadline]) : deadline
      if (pending.current) return resume(lease)
      const workspace = await prepare(lease)
      await ops.resume(workspace, lease)
      await refresh()
      setGeneration((value) => value + 1)
    },
    [prepare, refresh, resume]
  )
  useEffect(() => {
    mounted.current = options.enabled
    const controller = new AbortController()
    observation.current = controller.signal
    if (options.enabled) {
      // Observe only after saved sends are reconciled; replay can otherwise finish after the first idle snapshot.
      const initialization = options.sessionKey || id.current ? connect(observation.current) : Promise.resolve()
      void initialization.catch((cause: unknown) => {
        if (!controller.signal.aborted) fail(cause)
      })
    }
    return () => {
      mounted.current = false
      controller.abort()
      reading.current = null
    }
  }, [options.enabled, options.sessionKey, connect, refresh, fail])
  const active = snapshot?.requests.find((row) => !row.terminal)
  const nativeApproval = snapshot?.active?.status === 'waiting_for_approval' ? snapshot.active.approval : undefined
  useEffect(() => {
    waitingResumeAfter.current = active?.waiting?.resume_after ?? null
  }, [active?.waiting?.resume_after])
  useEffect(() => {
    if (!options.enabled || !active || status === 'error') return
    let disposed = false
    let waitingRead: ReturnType<typeof setTimeout> | undefined
    // Readbacks recover final output even if the event stream was lost.
    const timer = setInterval(() => {
      // Skip ticks inside the read gap instead of queueing a delayed refresh. A waiting request can wait for hours.
      const minimumGap = streamConnected.current || active.waiting ? 30000 : 2000
      if (!disposed && Date.now() - lastReadAt.current >= minimumGap) void refresh().catch(fail)
    }, 3000)
    // Every relay read wakes the workspace, and a long budget wait is meant to let it sleep. Past the deadline, a
    // waiting request is read once just after its resume time — that read wakes the workspace, which then resumes
    // it — and then with backoff until it leaves `waiting`, when this effect restarts for the resumed run.
    let targetedResume: string | null = null
    let backoffStep = 0
    const followWaiting = () => {
      if (disposed) return
      const resumeAfter = waitingResumeAfter.current
      const resumeAt = resumeAfter ? Date.parse(resumeAfter) : Number.NaN
      let delay: number
      if (resumeAt > Date.now() && resumeAfter !== targetedResume) {
        delay = resumeAt - Date.now() + waitingResumeMargin
        // Browsers fire longer timers at once. Wait in steps that fit, without reading (which would wake the workspace).
        if (delay > maxTimerDelay) {
          waitingRead = setTimeout(followWaiting, maxTimerDelay)
          return
        }
        targetedResume = resumeAfter
        backoffStep = 0
      } else delay = waitingBackoff[Math.min(backoffStep++, waitingBackoff.length - 1)]!
      waitingRead = setTimeout(() => void refresh().then(followWaiting, fail), delay)
    }
    const deadline = setTimeout(() => {
      clearInterval(timer)
      if (disposed) return
      if (active.waiting) followWaiting()
      else setError('Observation paused after twenty minutes. Reconnect to check the original run.')
    }, 20 * 60000)
    return () => {
      disposed = true
      clearInterval(timer)
      clearTimeout(deadline)
      clearTimeout(waitingRead)
    }
  }, [options.enabled, status, active?.request_id, !!active?.waiting, generation, refresh, fail])
  useEffect(() => {
    // A waiting request's run already failed; the next run starts when the workspace resumes it.
    if (!options.enabled || !snapshot || !active?.run_id || active.waiting || !id.current) return
    const controller = new AbortController()
    const tools = new Map<string, { label: string; count: number }>()
    const runId = active.run_id
    const afterMessageId =
      outgoing?.requestId === active.request_id
        ? outgoing.afterMessageId
        : Number(snapshot.messages.findLast((message) => message.role === 'user')?.id ?? 0)
    let text = ''
    setStreamed(undefined)
    let eventRefresh: ReturnType<typeof setTimeout> | undefined
    setActivity('Thinking…')
    const onEvent = (parsed: z.infer<typeof runEventFields>) => {
      const { event, tool, args } = parsed
      if (event === 'message.delta' && typeof parsed.delta === 'string') text += parsed.delta
      if (event === 'message.interim' && typeof parsed.text === 'string')
        text += (parsed.already_streamed ? '' : parsed.text) + '\n\n'
      if (event === 'message.delta' || event === 'message.interim') setStreamed({ runId, afterMessageId, text })
      if (
        active.request_id === timing.current?.requestId &&
        ((event === 'message.delta' && typeof parsed.delta === 'string' && parsed.delta.length > 0) ||
          (event === 'message.interim' && typeof parsed.text === 'string' && parsed.text.length > 0))
      )
        timing.current?.mark('first_text_received', { run_id: runId })
      if (active.request_id === timing.current?.requestId && event === 'tool.started')
        timing.current?.mark('first_tool_observed', { run_id: runId })
      if (
        active.request_id === timing.current?.requestId &&
        ['run.completed', 'run.cancelled', 'run.failed', 'run.interrupted'].includes(event)
      )
        timing.current?.mark('terminal_observed', { run_id: runId, outcome: event.slice(4) })
      if (event === 'tool.started' && tool) {
        const label = activityVerb(activityTitle(tool, args), false)
        tools.set(tool, { label, count: (tools.get(tool)?.count ?? 0) + 1 })
        setActivity(label)
      } else if (event === 'tool.completed' && tool) {
        const pendingTool = tools.get(tool)
        if (pendingTool && pendingTool.count > 1) tools.set(tool, { ...pendingTool, count: pendingTool.count - 1 })
        else tools.delete(tool)
        setActivity([...tools.values()].at(-1)?.label ?? 'Reviewing results')
      } else if (!tools.size && event === 'message.delta') setActivity('Writing a response')
      else if (!tools.size && event === 'reasoning.available') setActivity('Thinking…')
      if (['message.interim', 'tool.started', 'tool.completed'].includes(event) && !eventRefresh)
        eventRefresh = setTimeout(
          () => {
            eventRefresh = undefined
            void refreshAfterEvent(5000).catch(fail)
          },
          Math.max(2000, lastReadAt.current + 5000 - Date.now())
        )
    }
    const follow = async () => {
      let lastSeq = 0
      // A stream that keeps dying hands observation to the snapshot reads above, reconnecting from the last sequence.
      for (let lost = 0; lost < 3 && !controller.signal.aborted;) {
        const result = await followRun(ops, await workspaceId(), runId, lastSeq, controller.signal, (event) =>
          onEvent(runEventFields.parse(event))
        ).finally(() => {
          streamConnected.current = false
        })
        if (result.ended === 'terminal') {
          setActivity('')
          clearTimeout(eventRefresh)
          void refreshAfterEvent().catch(fail)
          return
        }
        lastSeq = result.lastSeq
        lost++
        setActivity('Reconnecting to the original run…')
        await new Promise((resolve) => setTimeout(resolve, 2000))
      }
      void refreshAfterEvent().catch(fail)
    }
    streamConnected.current = true
    void follow().catch((cause: unknown) => {
      if (controller.signal.aborted) return
      // The relay refused the stream (paused, retired, unavailable): the next snapshot read reports it.
      if (!(cause instanceof RelayError)) throw cause
      streamConnected.current = false
      void refreshAfterEvent().catch(fail)
    })
    const deadline = setTimeout(() => controller.abort(), 20 * 60000)
    return () => {
      clearTimeout(eventRefresh)
      clearTimeout(deadline)
      streamConnected.current = false
      controller.abort()
    }
  }, [options.enabled, active?.request_id, active?.run_id, !!active?.waiting, generation, fail, refreshAfterEvent])
  const send = useCallback(
    async (message: UserMessage, settings: SendSettings) => {
      if (pending.current) throw Error('Reconnect to confirm your previous message before sending another.')
      rejectedMessage.current = false
      // The same op id before and after any retry; neither may allocate a replacement conversation or change the message.
      const requestId = settings.requestId ?? crypto.randomUUID()
      const openId = id.current ? undefined : crypto.randomUUID()
      const session = id.current ?? `pending_${requestId}`
      pending.current = {
        requestId,
        workspaceId: boundWorkspace.current,
        send: {
          session_id: session,
          ...(openId ? { open_id: openId } : {}),
          message,
          // The context travels as JSON; fields the composer left undefined are not part of it.
          context: JSON.parse(
            JSON.stringify({
              ...settings.clientContext,
              ...current.current.scope,
            })
          ) as PendingSend['context'],
          ...(settings.instructions ? { instructions: settings.instructions } : {}),
        },
      }
      if (boundWorkspace.current)
        pending.current.send = ops.retain(boundWorkspace.current, pending.current.send, requestId)
      timing.current = createChatTiming(
        {
          conversation_id: pending.current.send.session_id,
          request_id: requestId,
          observation_id: crypto.randomUUID(),
        },
        (event) => track(analyticsEvents.chatTiming, event)
      )
      timing.current.mark('send_started')
      setSending(true)
      setOutgoing({
        requestId,
        message: pending.current.send.message,
        afterMessageId: Number(snapshot?.messages.at(-1)?.id ?? 0),
      })
      // An initial history read may still be in flight. Capture its boundary before
      // admission so an older identical message cannot replace this local bubble.
      try {
        const initial = await reading.current
        if (initial)
          setOutgoing((value) =>
            value ? { ...value, afterMessageId: Number(initial.messages.at(-1)?.id ?? 0) } : value
          )
        await resume()
      } finally {
        setSending(false)
      }
    },
    [resume, snapshot?.messages]
  )
  const stop = useCallback(async () => {
    if (!id.current && !pending.current) return
    setStopping(true)
    const conversationId = id.current
    await (async () => {
      if (pending.current) {
        // The send is still on its way: the acknowledgement's run id is stopped as soon as it arrives.
        stopRequested.current = true
        return
      }
      const workspace = await workspaceId()
      const runId = active?.run_id ?? (await activeRunId(ops, workspace, conversationId!, current.current.scope))
      // A request without a live run is cancelled as an admission, including one the workspace holds as waiting.
      if (active && (!active.run_id || active.waiting))
        await ops.json(workspace, op({ name: 'session.cancel', args: { op_id: active.request_id } }), z.unknown())
      else if (runId) await ops.json(workspace, op({ name: 'run.stop', args: { run_id: runId } }), z.unknown())
      setStoppedLocally(true)
    })().catch((cause) => {
      setStopping(false)
      fail(cause)
      throw cause
    })
    await refresh()
  }, [active, refresh, fail])
  const retry = useCallback(
    async (message: UserMessage, settings: SendSettings) => {
      if (pending.current) return resume()
      // Observation recovery must not resend work that finished while disconnected.
      if (status === 'error' && !rejectedMessage.current) {
        await refresh()
        setStatus('ready')
        return
      }
      const saved = await refresh()
      if (!saved) return
      const last = saved.requests.at(-1)
      const interrupted =
        !rejectedMessage.current &&
        last?.run_id &&
        last?.terminal &&
        ['failed', 'interrupted'].includes(last.terminal.status)
      setActivity('Resuming your request…')
      const input = interrupted
        ? { role: 'user' as const, content: interruptedChatRetryMessage }
        : last?.terminal?.run_id === null
          ? last.request.message
          : message
      return send(input, {
        ...settings,
        clientContext: { ...settings.clientContext, ...(interrupted ? { retryOfRequestId: last.request_id } : {}) },
      })
    },
    [refresh, resume, send, status]
  )
  const loadOlder = useCallback(async () => {
    if (!id.current || loadingHistory) return
    setLoadingHistory(true)
    try {
      const page = await readSnapshot(
        ops,
        await workspaceId(),
        id.current,
        current.current.scope,
        olderOffset + chatHistoryPageSize,
        observation.current
      )
      setSnapshot((previous) =>
        previous ? { ...previous, messages: mergeHistory(previous.messages, page.messages) } : page
      )
      setOlderOffset((offset) => offset + chatHistoryPageSize)
      setHistoryComplete(!page.hasOlderMessages)
    } finally {
      setLoadingHistory(false)
    }
  }, [olderOffset, loadingHistory])
  const messages = useMemo(() => {
    const result: Message[] = []
    const history = snapshot?.messages ?? []
    // Saved history replaces the entire preview when the run reaches its retained terminal state.
    const preview =
      !active?.waiting && streamed?.runId === active?.run_id && streamed?.text.trim() ? streamed : undefined
    for (const message of history) {
      if (message.role !== 'user' && message.role !== 'assistant') continue
      // Retry and the workspace's resume are conversation controls; retain their instructions for Hermes, not chat bubbles.
      // The workspace writes its continuation prompt itself, so no admission's context joins it: a message the user
      // typed with the same words carries the context of its own admission and stays visible.
      if (
        message.role === 'user' &&
        typeof message.content === 'string' &&
        (message.content === interruptedChatRetryMessage ||
          (!message.client_context && message.content.startsWith(workspaceContinuationPrefix)))
      )
        continue
      // Runtime notices duplicate the outcome controls. Deployed snapshots can omit display_kind.
      if (
        message.role === 'assistant' &&
        (message.display_kind === 'failed_turn' ||
          message.content === 'Your request was not processed. Send it again if you still want me to carry it out.' ||
          message.content ===
            'This turn did not complete. Some actions may already have run; verify their effects before resending.')
      )
        continue
      const parts: Message['parts'] = []
      const previewed = preview && message.role === 'assistant' && Number(message.id) > preview.afterMessageId
      if (!previewed && typeof message.content === 'string' && message.content)
        parts.push({
          type: 'text',
          text:
            message.role === 'user' && !message.client_context
              ? historicalMessageText(message.content)
              : message.content,
        })
      if (!previewed && Array.isArray(message.content))
        for (const block of message.content) {
          if (isUserMessage({ role: 'user', content: [block] })) parts.push(block as ContentPart)
        }
      const reasoning = message.reasoning_content ?? message.reasoning
      if (reasoning) parts.push({ type: 'reasoning', text: reasoning })
      for (const call of message.tool_calls ?? []) {
        const output = history.find((m) => m.role === 'tool' && m.tool_call_id === call.id)
        if (output)
          parts.push({
            type: 'dynamic-tool',
            toolCallId: call.id,
            toolName: call.function.name,
            input: JSON.parse(call.function.arguments),
            output: output.content,
            state: 'output-available',
          })
      }
      result.push({ id: String(message.id), role: message.role, parts, metadata: { status: 'complete' } })
    }
    for (const request of snapshot?.requests ?? [])
      if (
        !request.run_id &&
        !startedRequests.current.has(request.request_id) &&
        request.terminal?.status !== 'completed' &&
        request.request_id !== outgoing?.requestId &&
        messageText(request.request.message) !== interruptedChatRetryMessage
      )
        result.push({
          id: `pending:${request.request_id}`,
          role: 'user',
          parts: contentParts(request.request.message),
          metadata: { status: 'queued' },
        })
    if (
      outgoing &&
      messageText(outgoing.message) !== interruptedChatRetryMessage &&
      !history.some((message) => {
        const input = { role: 'user', content: message.content }
        const text =
          typeof input.content === 'string' ? input.content : isUserMessage(input) ? messageText(input) : undefined
        return (
          message.role === 'user' &&
          Number(message.id) > outgoing.afterMessageId &&
          text === messageText(outgoing.message)
        )
      })
    )
      result.push({
        id: `pending:${outgoing.requestId}`,
        role: 'user',
        parts: contentParts(outgoing.message),
        metadata: { status: startedRequests.current.has(outgoing.requestId) ? 'complete' : 'queued' },
      })
    if (preview)
      result.push({
        id: `stream:${preview.runId}`,
        role: 'assistant',
        parts: [{ type: 'text', text: preview.text }],
        metadata: { status: 'streaming' },
      })
    return result
  }, [snapshot, outgoing, streamed, active?.run_id, active?.waiting])
  useEffect(() => {
    if (outgoing && messages.some((message) => message.id === `pending:${outgoing.requestId}`))
      timing.current?.mark('message_committed')
  }, [messages, outgoing])
  const events = useMemo(() => chatEvents(snapshot?.requests ?? []), [snapshot])
  return {
    data: { messages },
    events,
    status,
    send,
    retry,
    resume,
    control: {
      loaded: snapshot !== undefined,
      sending,
      connect,
      nativeApproval,
      decidingApproval,
      canDecideApproval: !!snapshot?.active && !isTerminalRun(snapshot.active),
      decideApproval: async (choice: 'once' | 'deny') => {
        if (!nativeApproval || !snapshot?.active || !id.current) return
        setDecidingApproval(true)
        try {
          await ops
            .json(
              await workspaceId(),
              op({
                name: 'run.approve',
                args: { run_id: snapshot.active.run_id, request_id: nativeApproval.request_id, choice },
              }),
              z.unknown()
            )
            .catch((cause: unknown) => {
              if (!(cause instanceof RelayError && cause.kind === 'approval_expired')) throw cause
            })
          await refresh()
        } finally {
          if (mounted.current) setDecidingApproval(false)
        }
      },
      recordAssistantCommit: (messageId: string) => {
        if (outgoing && (messageId === `stream:${active?.run_id}` || Number(messageId) > outgoing.afterMessageId))
          timing.current?.mark('assistant_text_committed')
      },
      conversationId: snapshot?.conversationId,
      loadOlder,
      loadingHistory,
      hasOlderMessages: !!snapshot?.hasOlderMessages && !historyComplete,
      stop,
      check: async () => {
        setChecking(true)
        try {
          return await refresh()
        } finally {
          if (mounted.current) setChecking(false)
        }
      },
      checking,
      observationFailed,
      reportError: fail,
      stopping,
      stopped: stoppedLocally,
      error,
      activity: nativeApproval ? 'Waiting for your approval' : activity,
      active: !!active,
    },
  }
}
/** The native event fields the composer reads for activity and streamed text. */
const runEventFields = z
  .object({
    event: z.string(),
    tool: z.string().nullish(),
    args: z.unknown().optional(),
    delta: z.unknown().optional(),
    text: z.unknown().optional(),
    already_streamed: z.unknown().optional(),
  })
  .loose()
export const isConversationId = (value: string) => sessionIdPattern.test(value)

export type HermesChat = ReturnType<typeof useHermesChat>
